import { useState } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { money } from "@/lib/format";
import { P } from "@/lib/permissions";
import type { LedgerEntry, Paged, Payment, WalletSummary } from "@/lib/types";
import { Alert, Button, Card, Field, PageHeader, Pager, Skeleton, Stat, cx } from "@/components/ui";
import { LedgerTable } from "@/components/LedgerTable";
import { useAuth } from "@/state/auth";

const PRESETS = [20000, 50000, 100000, 500000];
const PAGE_SIZE = 25;

export function Wallet() {
  const { can } = useAuth();
  const [amount, setAmount] = useState(50000);
  const [custom, setCustom] = useState("");
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState("");

  const { data: wallet, isLoading } = useQuery({
    queryKey: ["wallet"],
    queryFn: () => api.get<{ wallet: WalletSummary; recent: LedgerEntry[] }>("/v1/wallet"),
    // A top-up lands via a gateway callback, not in this browser, so the page
    // polls rather than waiting for the customer to refresh.
    refetchInterval: 15_000,
  });

  const { data: statement } = useQuery({
    queryKey: ["statement", offset],
    queryFn: () => api.get<Paged<LedgerEntry>>(`/v1/wallet/statement?limit=${PAGE_SIZE}&offset=${offset}`),
    placeholderData: keepPreviousData,
  });

  const topup = useMutation({
    mutationFn: () =>
      api.post<{ payment: Payment; redirect_url: string }>("/v1/wallet/topups", {
        amount_cents: custom ? Math.round(Number(custom) * 100) : amount,
      }),
    onMutate: () => setError(""),
    onSuccess: (result) => {
      // The gateway owns the next step. The wallet is credited by its callback,
      // never by this browser coming back to a success page.
      if (result.redirect_url) window.location.href = result.redirect_url;
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.fields.amount_cents ?? err.message);
      } else {
        setError("Could not start the payment. Try again.");
      }
    },
  });

  const summary = wallet?.wallet;

  return (
    <>
      <PageHeader title="Wallet" subtitle="Your balance, top-ups and every charge." />

      {summary?.low_balance && (
        <div className="mb-5">
          <Alert tone="attention">
            Your balance is low. Top up to avoid a booking being declined mid-collection.
          </Alert>
        </div>
      )}

      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <Stat
          label="Balance"
          value={money(summary?.balance_cents)}
          loading={isLoading}
          tone={(summary?.balance_cents ?? 0) < 0 ? "bad" : "neutral"}
          hint={
            summary && summary.credit_limit_cents > 0
              ? `${money(summary.available_cents)} available including credit`
              : undefined
          }
        />
        <Stat label="Spent this month" value={money(summary?.spend_mtd_cents)} loading={isLoading} />
        <Stat label="Topped up this month" value={money(summary?.topped_up_mtd_cents)} loading={isLoading} />
      </div>

      {can(P.manageBilling) && (
        <Card className="mb-6">
          <h2 className="text-lg font-bold tracking-tight">Top up</h2>
          <p className="mb-4 mt-1 text-sm text-ink-500">
            Card or Instant EFT. Your balance updates the moment the payment clears.
          </p>

          {error && (
            <div className="mb-4">
              <Alert>{error}</Alert>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {PRESETS.map((preset) => (
              <button
                key={preset}
                onClick={() => {
                  setAmount(preset);
                  setCustom("");
                }}
                className={cx(
                  "rounded-xl border px-4 py-2 text-sm font-semibold transition",
                  amount === preset && !custom
                    ? "border-brand bg-brand-soft text-brand"
                    : "border-ink-300 bg-white text-ink-700 hover:border-ink-400",
                )}
              >
                {money(preset)}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <Field
              label="Or another amount (R)"
              type="number"
              min={50}
              className="w-48"
              value={custom}
              onChange={(event) => setCustom(event.target.value)}
              placeholder="750"
            />
            <Button loading={topup.isPending} onClick={() => topup.mutate()}>
              Continue to payment
            </Button>
          </div>
          <p className="mt-3 text-xs text-ink-400">Minimum R50, maximum R100,000 per payment.</p>
        </Card>
      )}

      <h2 className="mb-3 text-lg font-bold tracking-tight">Statement</h2>
      {isLoading ? (
        <Card>
          <Skeleton className="h-64 w-full" />
        </Card>
      ) : !statement?.data.length ? (
        <Card>
          <p className="text-sm text-ink-500">
            Nothing has moved on this wallet yet. Top up and your first transaction appears here.
          </p>
        </Card>
      ) : (
        <>
          <LedgerTable entries={statement.data} />
          <Pager total={statement.meta.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
        </>
      )}
    </>
  );
}
